// WorldKnowsRing — radial "how well the world knows you" ring (WU-W1).
// Fed by GET /api/profile/completion (completionPercentage) for now; the public
// `percent` prop is kept stable so a later WU can swap the feed to a traits-
// derived known-ness score with a one-line call-site change. Geometry per
// WU-D1 §D-3 (viewBox 120, r=52, C=326.726, arc clockwise from 12 o'clock).

import './WorldKnowsRing.css';

// Forward-compatible: the 7 per-lens trait dimensions. Accepted but not yet
// rendered (only the main ring shows until /api/profile/traits is wired).
interface TraitSegments {
  [dimension: string]: { value: number; confidence: number; sampleSize: number };
}

interface WorldKnowsRingProps {
  percent: number;
  label?: string;
  milestone?: boolean;
  segments?: TraitSegments;
}

const RADIUS = 52;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS; // 326.726

function clamp01(n: number): number {
  if (Number.isNaN(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

export function WorldKnowsRing({ percent, label, milestone }: WorldKnowsRingProps) {
  const fraction = clamp01(percent / 100);
  const rounded = Math.round(clamp01(percent / 100) * 100);
  const dashOffset = CIRCUMFERENCE * (1 - fraction);
  const isMilestone = milestone ?? rounded >= 100;

  return (
    <div
      className={`world-knows-ring ${isMilestone ? 'wkr-milestone' : ''}`}
      role="img"
      aria-label={`${rounded}% — the world is getting to know you`}
    >
      <svg className="wkr-svg" viewBox="0 0 120 120" aria-hidden="true">
        <defs>
          <linearGradient id="wkr-arc-grad" x1="0" y1="0" x2="1" y2="1">
            <stop className="wkr-arc-grad-start" offset="0%" />
            <stop className="wkr-arc-grad-end" offset="100%" />
          </linearGradient>
        </defs>
        <circle className="wkr-track" cx="60" cy="60" r={RADIUS} />
        <circle
          className="wkr-arc"
          cx="60"
          cy="60"
          r={RADIUS}
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={dashOffset}
        />
      </svg>
      <div className="wkr-center-label">
        {isMilestone && (
          <span className="wkr-star" aria-hidden="true">
            ⭐
          </span>
        )}
        <span className="wkr-percent">{rounded}%</span>
        <span className="wkr-caption">{label ?? 'the world knows you'}</span>
      </div>
    </div>
  );
}

export default WorldKnowsRing;
